import React from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import './PipelineStageChart.css';

/**
 * PipelineStageChart Component
 * Kanban-style horizontal view of candidates by pipeline stage
 */
function PipelineStageChart({ data, loading }) {
    if (loading) {
        return (
            <div className="pipeline-stage-chart chart-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="pipeline-stage-chart chart-empty">
                <p>No pipeline data available</p>
            </div>
        );
    }

    // Color gradient for stages
    const stageColors = {
        'New': '#3498DB',
        'New Lead': '#5DADE2',
        'Phone Screen': '#48C9B0',
        'Submittal': '#52BE80',
        'Submitted to HM': '#58D68D',
        'HM 1st Interview': '#F4D03F',
        'HM Interview': '#F5B041',
        'HM 2nd Interview': '#EB984E',
        'Final Interview': '#E59866',
        'Market Visit': '#EC7063',
        'Offer Extended': '#AF7AC5',
        'Offer Accepted': '#A569BD',
    };

    const getStageColor = (stage) => {
        return stageColors[stage] || '#E67E22';
    };

    const totalCandidates = data.reduce((sum, stage) => sum + stage.count, 0);

    // Calculate dynamic height based on number of stages (45px per stage, minimum 300px)
    const chartHeight = Math.max(300, data.length * 45);

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const d = payload[0].payload;
            const percentage = totalCandidates > 0
                ? ((d.count / totalCandidates) * 100).toFixed(1)
                : 0;
            return (
                <div className="chart-tooltip">
                    <p className="chart-tooltip-label">{d.stage}</p>
                    <p className="chart-tooltip-item">
                        <strong>{d.count}</strong> candidates ({percentage}%)
                    </p>
                    <p className="chart-tooltip-item">
                        Avg {d.avgDaysInStage} days in stage
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="pipeline-stage-chart">
            <div className="chart-header">
                <div className="chart-title-section">
                    <h3 className="chart-title">Active Pipeline by Stage</h3>
                    <span className="chart-subtitle">{totalCandidates} total active candidates</span>
                </div>
            </div>

            {/* Stage Cards View */}
            <div className="stage-cards">
                {data.map((stage, index) => (
                    <div
                        key={index}
                        className="stage-card"
                        style={{ borderTopColor: getStageColor(stage.stage) }}
                    >
                        <div className="stage-card-header">
                            <span className="stage-card-name">{stage.stage}</span>
                            <span
                                className="stage-card-count"
                                style={{ color: getStageColor(stage.stage) }}
                            >
                                {stage.count}
                            </span>
                        </div>
                        <div className="stage-card-meta">
                            <span className={`stage-avg-days ${stage.avgDaysInStage > 7 ? 'warning' : ''} ${stage.avgDaysInStage > 14 ? 'danger' : ''}`}>
                                {stage.avgDaysInStage}d avg
                            </span>
                        </div>
                        <div className="stage-card-bar">
                            <div
                                className="stage-card-bar-fill"
                                style={{
                                    width: `${totalCandidates > 0 ? (stage.count / totalCandidates) * 100 : 0}%`,
                                    background: getStageColor(stage.stage)
                                }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Bar Chart View */}
            <div className="stage-bar-chart">
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ top: 10, right: 30, left: 100, bottom: 10 }}
                    >
                        <XAxis type="number" hide />
                        <YAxis
                            type="category"
                            dataKey="stage"
                            tick={{ fontSize: 14, fill: '#2C3E50', fontWeight: 500 }}
                            axisLine={false}
                            tickLine={false}
                            width={110}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                            dataKey="count"
                            radius={[0, 4, 4, 0]}
                            barSize={22}
                        >
                            {data.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={getStageColor(entry.stage)}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default PipelineStageChart;
